/**
 * Notifications API Service
 * Handles all API calls for notification management
 */

import { apiClient } from './client';

export interface NotificationMetadata {
  symbol?: string;
  interval?: string;
  strategy?: string;
  duration?: number;
  summary?: {
    totalReturn?: number;
    sharpeRatio?: number;
    tradeCount?: number;
    winRate?: number;
  };
}

export interface Notification {
  id: string;
  type: 'optimization_complete' | 'wfa_complete' | 'backtest_complete' | 
        'optimization_failed' | 'wfa_failed' | 'backtest_failed' | 
        'system' | 'info';
  title: string;
  message: string;
  jobType?: 'optimization' | 'wfa' | 'backtest';
  jobId?: string;
  actionUrl?: string;
  read: boolean;
  metadata?: NotificationMetadata;
  createdAt: string;
}

export interface NotificationPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface GetNotificationsResponse {
  success: boolean;
  data: Notification[];
  pagination: NotificationPagination;
}

export interface GetUnreadCountResponse {
  success: boolean;
  data: { count: number };
}

export interface MarkAsReadResponse {
  success: boolean;
  data: Notification;
}

export interface MarkAllAsReadResponse {
  success: boolean;
  data: { modifiedCount: number };
}

/**
 * Get notifications for the current user
 */
export async function getNotifications(options: {
  page?: number;
  limit?: number;
  includeRead?: boolean;
} = {}): Promise<GetNotificationsResponse> {
  const { page = 1, limit = 20, includeRead = true } = options;
  
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    includeRead: String(includeRead)
  });
  
  return apiClient.get<GetNotificationsResponse>(`/notifications?${params}`);
}

/**
 * Get unread notifications only
 */
export async function getUnreadNotifications(limit = 10): Promise<GetNotificationsResponse> {
  return apiClient.get<GetNotificationsResponse>(`/notifications/unread?limit=${limit}`);
}

/**
 * Get unread notification count for badge
 */
export async function getUnreadCount(): Promise<number> {
  const response = await apiClient.get<GetUnreadCountResponse>('/notifications/unread-count');
  return response.data?.count ?? 0;
}

/**
 * Mark a notification as read
 */
export async function markAsRead(notificationId: string): Promise<MarkAsReadResponse> {
  return apiClient.patch<MarkAsReadResponse>(`/notifications/${notificationId}/read`, {});
}

/**
 * Mark all notifications as read
 */
export async function markAllAsRead(): Promise<MarkAllAsReadResponse> {
  return apiClient.patch<MarkAllAsReadResponse>('/notifications/read-all', {});
}

/**
 * Dismiss a notification
 */
export async function dismissNotification(notificationId: string): Promise<{ success: boolean }> {
  return apiClient.delete<{ success: boolean }>(`/notifications/${notificationId}`);
}

export const notificationsApi = {
  getNotifications,
  getUnreadNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  dismissNotification
};
